/**
 * Verfahrensdokumentation template.
 *
 * GoBD Rz. 151-154 requires one, and Rz. 155 warns that a missing or
 * inadequate one can defeat the formal ordnungsmäßigkeit of the whole
 * Buchführung. The parts the app can state about itself are pre-filled from
 * the live settings; the parts only the operator knows are left as marked
 * gaps, because a template that invents the operator's procedures is worse
 * than no template.
 */

import type { AccountRecord } from '@/crypto/vault';
import { formatDate, formatDateTime, nowIso, todayIso } from '@/domain/dates';
import { formatKm } from '@/domain/money';
import type { AppSettings, CompanySettings } from '@/domain/types';

const TODO = '**[bitte ergänzen]**';

export function buildVerfahrensdokumentation(
  company: CompanySettings,
  app: AppSettings,
  driver: AccountRecord,
): string {
  const licence =
    company.licenceType === 'taxi'
      ? 'Taxenverkehr nach § 47 PBefG'
      : 'Mietwagenverkehr nach § 49 PBefG';

  return `# Verfahrensdokumentation

## Fahrtenbuch und Rechnungsausgang

**Unternehmen:** ${company.companyName || TODO}
**Inhaber:** ${company.ownerName || TODO}
**Anschrift:** ${[company.street, `${company.postalCode} ${company.city}`.trim()].filter(Boolean).join(', ') || TODO}
**Steuernummer:** ${company.taxNumber || TODO}
**USt-IdNr.:** ${company.vatId || '— nicht vorhanden —'}
**Konzession:** ${licence}
**Stand:** ${formatDate(todayIso())}
**Erstellt:** ${formatDateTime(nowIso())} durch Fahrtenbuch Pro

> Diese Vorlage ist maschinell aus den Einstellungen der Anwendung erzeugt.
> Die mit ${TODO} markierten Stellen beschreiben betriebliche Abläufe, die nur
> Sie kennen. Eine Verfahrensdokumentation, die diese Stellen offen lässt,
> erfüllt GoBD Rz. 151–154 nicht.

---

## 1. Allgemeine Beschreibung

### 1.1 Zweck

Aufzeichnung der Beförderungsleistungen im ${licence} sowie Erstellung der
Ausgangsrechnungen. Die Aufzeichnungen dienen der Erfüllung der Pflichten nach
§§ 140–148 AO, insbesondere der Einzelaufzeichnungspflicht nach § 146 Abs. 1
Satz 1 AO, sowie der Aufzeichnung des Auftragseingangs nach § 49 Abs. 4 Satz 4
PBefG.

Der Feldkatalog folgt dem BMF-Schreiben vom 11.03.2024
(IV D 2 - S 0316-a/21/10006 :008), Abschnitt III (Mietwagenunternehmen).

### 1.2 Sachlicher Anwendungsbereich

- Erfassung jeder einzelnen Beförderung als Geschäftsvorfall
- Arbeitstägliche Aufzeichnung von Kilometerstand und Einnahmen je Schicht
- Erfassung des Eingangs der Beförderungsaufträge
- Erstellung, Nummerierung und Verwaltung der Ausgangsrechnungen

Nicht erfasst: ${TODO} — z. B. Kassenbuch, Wareneingang, Lohnabrechnung, Anlagenbuchhaltung.

### 1.3 Organisatorischer Rahmen

**Verantwortlich für die Ordnungsmäßigkeit:** ${company.ownerName || TODO}
**Erfassende Personen:** ${driver.displayName} (Fahrerkennung ${driver.driverCode})${
    ' und ' + TODO
  }
**Vertretungsregelung:** ${TODO}
**Steuerliche Beratung:** ${TODO}

---

## 2. Anwenderdokumentation

### 2.1 Eingesetzte Software

| Merkmal | Angabe |
|---|---|
| Bezeichnung | Fahrtenbuch Pro |
| Art | Progressive Web App, vollständig lokal |
| Version | ${TODO} — im Browser unter Einstellungen ablesbar |
| Hersteller | ${TODO} |
| Datenhaltung | IndexedDB im Browser des Endgeräts, verschlüsselt |
| Serverkomponente | keine |

### 2.2 Eingesetzte Hardware

| Merkmal | Angabe |
|---|---|
| Endgerät | ${TODO} — z. B. iPhone 15, Modellnummer, Seriennummer |
| Betriebssystem | ${TODO} |
| Browser | ${TODO} |
| Installationsart | ${TODO} — z. B. zum Home-Bildschirm hinzugefügt am TT.MM.JJJJ |

### 2.3 Ablauf der Erfassung

1. **Auftragseingang.** Sobald ein Beförderungsauftrag eingeht, wird er unter
   „Aufträge" erfasst — mit laufender Nummer, Eingangszeitpunkt, Kanal,
   Besteller, Abholstelle und Zielort. Bei der Übernahme des Auftrags werden
   Ort der Übernahme (Betriebssitz, Wohnung oder fernmündlich während der
   Fahrt), Zeitpunkt, Fahrzeug und Fahrer nachgetragen. Die Erfassung des
   Eingangs geschieht vor Antritt der Fahrt. Feldfolge und Bezeichnungen
   entsprechen dem Auftrag-Eingangs-Buch für Mietwagenunternehmer.
2. **Fahrt.** Nach Abschluss der Fahrt werden Fahrgast, Abhol- und Zieladresse,
   Zeiten, Strecke, Entgelt, Umsatzsteuersatz, Zahlungsart und gegebenenfalls
   Trinkgeld erfasst.
3. **Schicht.** Zu Beginn des Arbeitstages werden Arbeitsanfang und
   Kilometerstand laut Tachometer erfasst, zum Schichtende Arbeitsende und
   Endstand; Pausen werden mit Beginn und Ende eingetragen. Die Anwendung
   gleicht die Summe der Fahrtstrecken gegen die Tachodifferenz ab. Der
   Schichtabschluss erfolgt namentlich durch die fahrende Person und tritt an
   die Stelle der Unterschrift auf dem Fahrtennachweis.
4. **Rechnung.** Erfasste Fahrten werden zu einer Rechnung zusammengefasst. Die
   Rechnungsnummer wird erst bei der Ausstellung vergeben.
5. **Datensicherung.** ${TODO} — z. B. wöchentlich freitags, Ablage auf
   verschlüsseltem USB-Stick im Büro.

**Zeitliche Vorgabe:** Fahrten werden am Tag der Durchführung erfasst,
spätestens jedoch innerhalb von ${app.recordLockDays} Kalendertagen. Nach Ablauf
dieser Frist schreibt die Anwendung den Datensatz automatisch fest.

### 2.4 Abweichungen vom Regelablauf

${TODO} — z. B. Vorgehen bei Geräteausfall, bei nachträglich bekannt werdenden
Korrekturen, bei Fahrten ohne vorherigen Auftragseingang.

---

## 3. Technische Systemdokumentation

### 3.1 Datenmodell

| Tabelle | Inhalt |
|---|---|
| \`trips\` | Einzelne Beförderungen |
| \`shifts\` | Arbeitstägliche Aufzeichnungen je Fahrer und Fahrzeug |
| \`orders\` | Auftragseingang nach § 49 Abs. 4 Satz 4 PBefG |
| \`passengers\`, \`addresses\` | Lernende Stammdaten mit Nutzungszähler |
| \`vehicles\` | Fahrzeuge mit Ordnungsnummer und Kennzeichen |
| \`invoices\` | Ausgangsrechnungen mit Positionen |
| \`auditLog\` | Verkettetes Änderungsprotokoll |
| \`settings\` | Firmen- und Anwendungseinstellungen |

### 3.2 Verschlüsselung

Aus dem Anmeldepasswort wird mit Argon2id (64 MiB Speicher, 3 Durchläufe, 32
Byte Ausgabe) ein Schlüssel abgeleitet. Dieser entschlüsselt einen zufällig
erzeugten 256-Bit-Datenschlüssel, mit dem alle Datensätze per AES-256-GCM
verschlüsselt in der Datenbank abgelegt werden. Der Datenschlüssel ist
zusätzlich unter einem Wiederherstellungscode hinterlegt.

Im Klartext gespeichert werden ausschließlich nicht identifizierende
Indexfelder: technische Kennungen, Datum, Monatsschlüssel und Statusmerkmale.

### 3.3 Unveränderbarkeit

Datensätze werden bei Erfassung geschrieben und nicht überschrieben. Jede
Änderung erzeugt einen Eintrag im Änderungsprotokoll, der den vollständigen
Zustand vor und nach der Änderung sowie eine feldweise Gegenüberstellung
enthält.

Jeder Protokolleintrag trägt:

- eine lückenlose laufende Nummer, beginnend bei 1,
- den SHA-256-Hash des vorhergehenden Eintrags,
- den eigenen SHA-256-Hash über die kanonische JSON-Darstellung aller übrigen
  Felder (Schlüssel alphabetisch sortiert, keine zusätzlichen Leerzeichen).

Ein Eingriff in die Datenbank außerhalb der Anwendung bricht die Kette an
nachweisbarer Stelle. Die Kette wird bei jedem Export vollständig geprüft; das
Prüfergebnis ist Bestandteil des Exports.

Die Änderungshistorie wird in der Detailansicht des Datensatzes und in jedem
Export unmittelbar bei dem betroffenen Datensatz ausgewiesen — nicht als
Anhang und nicht auf gesonderte Anforderung (BFH VI B 37/23 vom 12.01.2024).

### 3.4 Festschreibung

Ein Datensatz wird ${app.recordLockDays} Kalendertage nach dem Fahrtdatum
automatisch festgeschrieben. Danach ist eine Korrektur nur mit Angabe einer
Begründung möglich; Begründung und Änderung werden protokolliert und in jedem
Export ausgewiesen.

### 3.5 Ermittlung der Fahrtstrecke

**Maßgeblich ist:** ${
    app.distanceSystemOfRecord === 'computed'
      ? 'die von einem Routing-Dienst berechnete Fahrstrecke'
      : 'der Kilometerstand laut Tachometer'
  }

${
  app.distanceSystemOfRecord === 'computed'
    ? `Die Adressen werden über Geoapify (EU-Endpunkt \`api-eu.geoapify.com\`)
geokodiert; die Fahrstrecke wird als Straßenroute berechnet. Der Wert ist im
Erfassungsformular überschreibbar; jede Abweichung wird als Änderung
protokolliert und im Feld \`StreckeKorrigiert\` des Exports ausgewiesen.

Der arbeitstägliche Kilometerstand wird unabhängig davon erfasst und gegen die
Summe der Fahrtstrecken abgeglichen. Die Toleranz ist auf
${formatKm(app.reconciliationToleranceMeters)} km je Schicht eingestellt;
darüber hinausgehende Abweichungen werden gekennzeichnet.

**Hinweis zum offenen Rechtsrisiko:** Seit dem 01.02.2026 erfasst
§ 1 Abs. 2 Satz 2 KassenSichV app-basierte Systeme, „sofern diese die Funktion
eines EU-Taxameters oder Wegstreckenzählers übernehmen". Ob eine Anwendung, die
die Fahrstrecke selbst ermittelt und als maßgeblichen Wert führt, hierunter
fällt — mit der Folge einer zertifizierten technischen Sicherheitseinrichtung
nach § 146a AO und einer Konformitätsbewertung nach Eichrecht (§ 30 Abs. 1
BOKraft) — ist bislang weder durch Rechtsprechung noch durch
Verwaltungsanweisung geklärt.

**Beurteilung des Unternehmens:** ${TODO} — bitte nach Rücksprache mit der
steuerlichen Beratung ausfüllen.`
    : `Die berechnete Strecke wird nachrichtlich mitgeführt, ist aber nicht
maßgeblich für die Aufzeichnung.`
}

### 3.6 Laufende Nummer im Auftragseingang

Jeder Auftragseingang erhält eine laufende Nummer, die je Kalenderjahr bei 1
beginnt und lückenlos fortgeführt wird. Sie entspricht der Nummernfolge eines
gebundenen Auftrag-Eingangs-Buches, über die dessen Vollständigkeit
nachgewiesen wird. Der Zähler wird aus dem Bestand abgeleitet und nicht separat
gespeichert; fehlende Nummern werden in der Auftragsübersicht und im Export
ausgewiesen.

### 3.7 Rechnungsnummern

Nummernkreis: ${TODO} — Voreinstellung \`RE-JJJJ-NNNN\`, jährlich neu beginnend.

Die Nummer wird erst bei der Ausstellung vergeben, nicht beim Anlegen eines
Entwurfs, damit ein verworfener Entwurf keine Lücke hinterlässt. Der Zähler
wird aus den vorhandenen Rechnungen abgeleitet, nicht separat gespeichert.
Lücken werden in den Einstellungen und im Export ausgewiesen.

### 3.8 Umsatzsteuer

Der Steuersatz richtet sich nach der Konzessionsart, nicht nach der Entfernung.

${
  company.kleinunternehmer
    ? 'Es wird nach § 19 UStG abgerechnet; Umsatzsteuer wird nicht ausgewiesen.'
    : company.licenceType === 'taxi'
      ? 'Taxenverkehr nach § 47 PBefG: 7 % innerhalb einer Gemeinde oder bis 50 km Beförderungsstrecke, sonst 19 % (§ 12 Abs. 2 Nr. 10 Buchst. b UStG).'
      : 'Mietwagenverkehr nach § 49 PBefG: durchgehend 19 %. Der ermäßigte Satz nach § 12 Abs. 2 Nr. 10 Buchst. b UStG gilt nur für Taxen nach § 47 PBefG.'
}

---

## 4. Internes Kontrollsystem

| Kontrolle | Umsetzung |
|---|---|
| Zugangskontrolle | Passwortgeschütztes Konto je Fahrer; Daten ohne Passwort nicht lesbar |
| Automatische Sperre | Nach 10 Minuten ohne Bedienung wird der Schlüssel verworfen |
| Vollständigkeit | Abgleich der Fahrtstrecken gegen die Tachodifferenz je Schicht |
| Richtigkeit | Berechnete Strecke als Vorschlag, Abweichung protokolliert |
| Zeitgerechte Erfassung | Automatische Festschreibung nach ${app.recordLockDays} Tagen |
| Unveränderbarkeit | Verkettetes Protokoll, Prüfung bei jedem Export |
| Auftragseingang | Lückenlose laufende Nummer je Jahr, Lückenprüfung; Ort der Auftragsübernahme als geschlossene Auswahl nach § 49 Abs. 4 PBefG |
| Schichtabschluss | Namentliche Bestätigung durch die fahrende Person, im Protokoll mit Zeitstempel |
| Rechnungsnummern | Ableitung aus dem Bestand, Lückenprüfung |
| Datensicherung | Erinnerung nach ${app.backupWarnAfterDays} Tagen, Sperre des Monatsabschlusses nach ${app.backupBlockAfterDays} Tagen |
| Vier-Augen-Prinzip | ${TODO} |
| Turnusmäßige Prüfung | ${TODO} — z. B. monatlicher Abgleich mit den Kontoauszügen |

---

## 5. Datensicherung

**Verfahren:** Verschlüsselte Sicherungsdatei aus der Anwendung, zusätzlich das
strukturierte GoBD-Archiv.

**Turnus:** ${TODO} — die Anwendung erinnert nach ${app.backupWarnAfterDays} Tagen.
**Ablageort:** ${TODO}
**Verantwortlich:** ${TODO}
**Wiederherstellung geprüft am:** ${TODO}

Die Sicherung ist im Geltungsbereich der Abgabenordnung oder in der EU
aufzubewahren (§ 146 Abs. 2, 2a AO). Eine Ablage bei einem Anbieter außerhalb
der EU kann eine Bewilligung nach § 146 Abs. 2b AO erfordern.

**Aufbewahrungsfristen**

| Unterlage | Frist | Grundlage |
|---|---|---|
| Fahrtaufzeichnungen | 10 Jahre | § 147 Abs. 1 Nr. 1, Abs. 3 AO |
| Buchungsbelege | 8 Jahre | § 147 Abs. 1 Nr. 4, Abs. 3 AO |
| Auftragseingang | 1 Jahr (steuerlich 6 Jahre) | § 49 Abs. 4 Satz 4 PBefG |
| Diese Dokumentation | 10 Jahre | § 147 Abs. 1 Nr. 1 AO |

Die Frist beginnt mit Ablauf des Kalenderjahres der letzten Eintragung
(§ 147 Abs. 4 AO).

---

## 6. Datenzugriff nach § 147 Abs. 6 AO

| Form | Bereitstellung |
|---|---|
| Z1 — unmittelbarer Zugriff | Nur-Lese-Einsicht am Endgerät im Beisein des Prüfers |
| Z2 — mittelbarer Zugriff | Auswertungen nach Vorgabe des Prüfers über die Exportfunktion |
| Z3 — Datenträgerüberlassung | GoBD-Archiv: CSV-Dateien mit \`index.xml\` im GDPdU-Beschreibungsstandard, Prüfprotokoll und vollständigem Änderungsprotokoll |

Das Archiv ist ohne Installation zusätzlicher Software lesbar. Zeichensatz
UTF-8, Feldtrennzeichen Semikolon, Textbegrenzer Anführungszeichen,
Dezimaltrenner Komma, Datumsformat TT.MM.JJJJ.

---

## 7. Änderungshistorie dieser Dokumentation

| Datum | Version | Änderung | Bearbeiter |
|---|---|---|---|
| ${formatDate(todayIso())} | 1.0 | Ersterstellung aus Vorlage | ${company.ownerName || TODO} |

---

*Diese Dokumentation ist nach § 147 Abs. 1 Nr. 1 AO aufzubewahren. Sie ist bei
jeder wesentlichen Änderung des Verfahrens fortzuschreiben; die
Versionsgeschichte muss erkennen lassen, welches Verfahren in welchem Zeitraum
gegolten hat (GoBD Rz. 154).*
`;
}
